import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Checkbox from '@repo/ui/checkbox'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { startOfDay } from 'date-fns'
import { TFunction } from 'i18next'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCenterJourneys } from '../../../../api/centers/get-center-journeys'
import { getActivities } from '../../../../api/ols/activities/get-activities'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  ActivityMemberEnum,
  activityMemberEnumMapping
} from '../../../../lib/types/enums/activity-member/activity-member.enum'
import { ProductTypeEnum } from '../../../../lib/types/enums/product/product-type.enum'
import { CenterActivityInput } from '../../../../lib/types/models/center-activity/center-activity-input'
import { JourneyProperties } from '../../../../lib/types/models/product/journey.properties'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getActivityBookableOptions } from '../../../../lib/utils/functions/center-activity.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { createTimeSchema } from '../../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import {
  HOST_FAMILY_DAY_ENDING_TIME,
  HOST_FAMILY_DAY_STARTING_TIME,
  isHostFamilyDayActivity
} from '../../../../utils/constants/activity.constants'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

const formSchema = (t: TFunction<AvailableLanguages, undefined>): ZodType<CenterActivityInput> =>
  z
    .object({
      date: createStringSchema(t('ol.center.program.activities.planner.activity.form.date')),
      startingTime: createTimeSchema({
        inputName: t('ol.center.program.activities.planner.activity.form.starting.time')
      }),
      endingTime: createTimeSchema({
        inputName: t('ol.center.program.activities.planner.activity.form.ending.time')
      }),
      activityId: createNumberSchema(
        t('ol.center.program.activities.planner.activity.form.activity.id')
      ),
      comment: createStringSchema(
        t('ol.center.program.activities.planner.activity.form.comment'),
        { nullish: true }
      ),
      activityPaidBy: createEnumSchema({
        enumType: ActivityMemberEnum,
        fieldName: t('ol.center.program.activities.planner.activity.form.activity.paid.by')
      }),
      journeyIds: createNumberArraySchema(
        t('ol.center.program.activities.planner.activity.form.journey.ids'),
        { errorMessage: t('ol.center.program.activities.planner.activity.form.journey.ids') }
      ),
      optionId: z.number().nullish()
    })
    .superRefine((data, ctx) => {
      // the starting time must be before the ending time
      const startingTime = data.startingTime
      const endingTime = data.endingTime

      if (DateFormatter.isTimeGreaterThanOrEqualTo(startingTime, endingTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('ol.center.program.activities.planner.activity.form.error.starting.time'),
          path: ['startingTime'] // This will add the error to the startingTime field
        })
      }
    })

type Props = BaseFormProps<CenterActivityInput, true> & {
  olId: number
  centerId: number
  className?: string
}

const CenterActivityForm = ({
  defaultValues,
  className,
  olId,
  centerId,
  ...validationButtonProps
}: Props) => {
  const { t, currentLanguage } = useTranslate()
  const form = useForm<CenterActivityInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })
  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => getActivities({ olId })
  })

  const { data: journeys } = useQuery({
    queryKey: ['center-journeys', centerId],
    queryFn: () => getCenterJourneys(centerId, 'BASIC')
  })

  // when adding an activity, all the programmes of the center are selected by default, except
  // in a center mixing Club 4 and One to One stays: an activity is never shared by both formulas
  useEffect(() => {
    if (!journeys || form.getValues('journeyIds')) return
    const productTypes = journeys.map((centerJourney) => centerJourney.journey.date.product.type)
    const isMixedFormulas =
      productTypes.includes(ProductTypeEnum.CLUB_FOUR) &&
      productTypes.includes(ProductTypeEnum.ONE_TO_ONE)
    if (isMixedFormulas) return
    form.setValue(
      'journeyIds',
      journeys.map((centerJourney) => centerJourney.journey.id)
    )
  }, [form, journeys])

  // an activity can be limited to the students who booked one option of the selected programmes
  const selectedJourneyIds = form.watch('journeyIds')
  const selectedOptionId = form.watch('optionId')
  const bookableOptions = useMemo(
    () =>
      getActivityBookableOptions(
        journeys?.map((centerJourney) => centerJourney.journey) ?? [],
        selectedJourneyIds
      ),
    [journeys, selectedJourneyIds]
  )

  // the option is dropped when the selected programmes no longer offer it
  useEffect(() => {
    if (!journeys || !selectedOptionId) return
    if (!bookableOptions.some((option) => option.id === selectedOptionId))
      form.setValue('optionId', null)
  }, [form, journeys, bookableOptions, selectedOptionId])

  const startDate = journeys?.[0]?.journey.date.departureDate
  const endDate = journeys?.[0]?.journey.date.returnDate

  const selectedActivityId = form.watch('activityId')
  const selectedActivity = activities?.data.find((activity) => activity.id === selectedActivityId)
  const isHostFamilyDay = !!selectedActivity && isHostFamilyDayActivity(selectedActivity)

  // "Host family day" has no schedule and is not payable: the time and paid by inputs are hidden
  // and fixed placeholder values are submitted
  useEffect(() => {
    if (!isHostFamilyDay) return
    form.setValue('startingTime', HOST_FAMILY_DAY_STARTING_TIME, { shouldValidate: true })
    form.setValue('endingTime', HOST_FAMILY_DAY_ENDING_TIME, { shouldValidate: true })
    form.setValue('activityPaidBy', ActivityMemberEnum.ORGANIZER, { shouldValidate: true })
  }, [form, isHostFamilyDay])

  if (!activities || !journeys) return <Loader center />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <FormField
            control={form.control}
            name="activityId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    disabled={!activities?.data.length}
                    label={t(
                      'ol.center.program.activities.planner.activity.form.activity.id.label'
                    )}
                    searchable
                    options={activities?.data.map((activity) => ({
                      label: activity.name,
                      value: activity.id
                    }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label:
                        activities?.data.find((activity) => activity.id === field.value)?.name ??
                        '-',
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    startDate={startDate ? startOfDay(startDate) : undefined}
                    endDate={endDate ? startOfDay(endDate) : undefined}
                    required
                    mode="over"
                    label={t('ol.center.program.activities.planner.activity.form.date.label')}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {!isHostFamilyDay && (
            <div className="flex items-center">
              <FormField
                control={form.control}
                name={'startingTime'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput
                        {...field}
                        required
                        label={t(
                          'ol.center.program.activities.planner.activity.form.starting.time.label'
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="w-3" />
              <FormField
                control={form.control}
                name={'endingTime'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput
                        {...field}
                        required
                        label={t(
                          'ol.center.program.activities.planner.activity.form.ending.time.label'
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}
          <div>
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              {t('ol.center.program.activities.planner.activity.form.journey.ids.label')}
            </Typography>
            <div className="h-2" />
            <div className="flex items-center gap-2">
              {journeys?.length ? (
                journeys.map((centerJourney) => (
                  <Checkbox
                    key={`journey-${centerJourney.journey.id}`}
                    checked={form.watch('journeyIds')?.includes(centerJourney.journey.id)}
                    onChange={(checked) => {
                      const currentJourneyIds = form.getValues('journeyIds') ?? []

                      if (checked) {
                        form.setValue('journeyIds', [
                          ...currentJourneyIds,
                          centerJourney.journey.id
                        ])
                      } else {
                        form.setValue(
                          'journeyIds',
                          currentJourneyIds.filter((id) => id !== centerJourney.journey.id)
                        )
                      }
                    }}
                    label={JourneyProperties.getProgramLabel(
                      centerJourney.journey,
                      currentLanguage
                    )}
                  />
                ))
              ) : (
                <Typography variant="secondary-body" color={tailwindColors.grey.lighter}>
                  {t(
                    'ol.center.program.activities.planner.activity.form.journey.ids.not.available'
                  )}
                </Typography>
              )}
            </div>
            {form.formState.errors.journeyIds && !form.watch('journeyIds') && (
              <Typography variant="small-medium" color={tailwindColors.red.DEFAULT}>
                {form.formState.errors.journeyIds.message}
              </Typography>
            )}
          </div>
          {bookableOptions.length > 0 && (
            <div>
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('ol.center.program.activities.planner.activity.form.option.id.label')}
              </Typography>
              <div className="h-2" />
              <FormField
                control={form.control}
                name="optionId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-col gap-2">
                        {[null, ...bookableOptions].map((option) => (
                          <ControlledRadioInput
                            key={`option-${option?.id ?? 'none'}`}
                            name={field.name}
                            labelClassName="text-xs font-medium text-grey"
                            label={
                              option
                                ? t(
                                    'ol.center.program.activities.planner.activity.form.option.id.option',
                                    { option: getTranslatedName(option, currentLanguage) }
                                  )
                                : t(
                                    'ol.center.program.activities.planner.activity.form.option.id.none'
                                  )
                            }
                            checked={(field.value ?? null) === (option?.id ?? null)}
                            onCheck={() => field.onChange(option?.id ?? null)}
                          />
                        ))}
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}
          <div>
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              {t('ol.center.program.activities.planner.activity.form.comment.label')}
            </Typography>
            <div className="h-2" />
            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="textarea"
                      placeholder={t(
                        'ol.center.program.activities.planner.activity.form.comment.placeholder'
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          {!isHostFamilyDay && (
            <div>
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('ol.center.program.activities.planner.activity.form.activity.paid.by.label')}
              </Typography>
              <div className="h-2" />
              <div className="flex items-center gap-14">
                {Object.values(ActivityMemberEnum).map((activityMember) => (
                  <FormField
                    key={activityMember}
                    control={form.control}
                    name="activityPaidBy"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            required
                            labelClassName="text-xs font-medium text-grey"
                            label={activityMemberEnumMapping[activityMember]}
                            checked={field.value === activityMember}
                            onCheck={() => field.onChange(activityMember)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default CenterActivityForm
